import { Controller } from '@hotwired/stimulus'
import { markerTemplateFor } from '../helpers'

export default class extends Controller {
  static targets = [
    'pasteInput', 'markersContent', 'cardsSection', 'cardsBody', 'rowCount',
    'submitButton', 'buttonCount', 'exampleDialog', 'scrollHint', 'templateChip'
  ]

  connect () {
    this.onScroll = this.onScroll.bind(this)
    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('resize', this.onScroll, { passive: true })
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.onScroll)
      this.resizeObserver.observe(document.body)
    }
    this.onScroll()
  }

  disconnect () {
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.onScroll)
    this.resizeObserver?.disconnect()
  }

  onScroll () {
    if (!this.hasScrollHintTarget) return
    const docHeight = document.documentElement.scrollHeight
    const viewport = window.innerHeight
    const scrolled = window.scrollY
    const distanceFromBottom = docHeight - (scrolled + viewport)
    const isScrollable = docHeight > viewport + 100
    const shouldShow = isScrollable && distanceFromBottom > 200
    this.scrollHintTarget.style.opacity = shouldShow ? '1' : '0'
  }

  clearContent () {
    this.pasteInputTarget.value = ''
    this.clearActiveTemplate()
    this.rebuildCards()
    this.pasteInputTarget.focus()
  }

  openExample () {
    this.exampleDialogTarget.showModal()
  }

  closeExample () {
    this.exampleDialogTarget.close()
  }

  dialogBackdropClick (event) {
    if (event.target === this.exampleDialogTarget) {
      this.exampleDialogTarget.close()
    }
  }

  changePasteInput () {
    this.clearActiveTemplate()
    this.rebuildCards()
  }

  applyTemplate (event) {
    const chip = event.currentTarget
    const key = chip.dataset.template
    const lines = markerTemplateFor(key)
    if (!lines) return
    this.pasteInputTarget.value = lines.join('\n')
    this.setActiveTemplate(chip)
    this.rebuildCards()
  }

  setActiveTemplate (activeChip) {
    if (!this.hasTemplateChipTarget) return
    this.templateChipTargets.forEach((chip) => {
      chip.dataset.active = chip === activeChip ? 'true' : 'false'
    })
  }

  clearActiveTemplate () {
    if (!this.hasTemplateChipTarget) return
    this.templateChipTargets.forEach((chip) => { chip.dataset.active = 'false' })
  }

  rebuildCards () {
    const markers = this.parseInput()
    if (markers.length === 0) {
      this.cardsSectionTarget.classList.add('hidden')
      this.cardsBodyTarget.innerHTML = ''
      this.rowCountTarget.textContent = '0'
      this.submitButtonVisible = false
      this.serializeMarkers()
      this.updateSubmitButtonState()
      requestAnimationFrame(() => this.onScroll())
      return
    }

    this.cardsBodyTarget.innerHTML = ''
    const cards = markers.map((marker, index) => {
      const card = this.createMarkerCard(marker, index)
      this.cardsBodyTarget.appendChild(card)
      return card
    })
    this.cardsSectionTarget.classList.remove('hidden')
    cards.forEach((c) => this.autosizeCard(c))
    this.rowCountTarget.textContent = String(markers.length)
    this.submitButtonVisible = true
    this.serializeMarkers()
    this.updateSubmitButtonState()
    requestAnimationFrame(() => this.onScroll())
  }

  parseInput () {
    return this.pasteInputTarget.value
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const commaIdx = line.lastIndexOf(',')
        if (commaIdx === -1) return { name: line, date: '' }
        return {
          name: line.slice(0, commaIdx).trim(),
          date: this.parseDateInput(line.slice(commaIdx + 1).trim())
        }
      })
  }

  createMarkerCard (marker, index) {
    const card = document.createElement('div')
    card.className = 'group rounded-2xl border border-gray-100 bg-white p-4 hover:shadow-100 transition'
    card.dataset.markerIndex = index

    const dateLabel = marker.date ? this.formatDateLabel(marker.date) : 'Add date'

    card.innerHTML = `
      <div class="flex items-start gap-3">
        <div class="flex-1 min-w-0">
          <textarea
            rows="1"
            placeholder="Marker name"
            class="block w-full bg-transparent border-0 p-0 text-base font-medium text-gray-900 focus:ring-0 focus:outline-none placeholder:text-gray-300 resize-none min-h-[1.5rem]"
            data-marker-name
            data-action="input->lineup-markers#onCardChange input->lineup-markers#autosize"></textarea>
        </div>

        <div class="flex items-center gap-1 shrink-0">
          <button type="button"
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 text-gray-700 text-xs font-medium hover:bg-gray-50 hover:border-gray-400 transition shrink-0"
                  data-action="click->lineup-markers#openDatePicker">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M16 2c.183 0 .355 .05 .502 .135l.033 .02c.28 .177 .465 .49 .465 .845v1h1a3 3 0 0 1 2.995 2.824l.005 .176v12a3 3 0 0 1 -2.824 2.995l-.176 .005h-12a3 3 0 0 1 -2.995 -2.824l-.005 -.176v-12a3 3 0 0 1 2.824 -2.995l.176 -.005h1v-1a1 1 0 0 1 .514 -.874l.093 -.046l.066 -.025l.1 -.029l.107 -.019l.12 -.007q .083 0 .161 .013l.122 .029l.04 .012l.06 .023c.328 .135 .568 .44 .61 .806l.007 .117v1h6v-1a1 1 0 0 1 1 -1m3 7h-14v9.625c0 .705 .386 1.286 .883 1.366l.117 .009h12c.513 0 .936 -.53 .993 -1.215l.007 -.16z"/>
              <path d="M9.015 13a1 1 0 0 1 -1 1a1.001 1.001 0 1 1 -.005 -2c.557 0 1.005 .448 1.005 1"/>
              <path d="M13.015 13a1 1 0 0 1 -1 1a1.001 1.001 0 1 1 -.005 -2c.557 0 1.005 .448 1.005 1"/>
              <path d="M17.02 13a1 1 0 0 1 -1 1a1.001 1.001 0 1 1 -.005 -2c.557 0 1.005 .448 1.005 1"/>
              <path d="M12.02 15a1 1 0 0 1 0 2a1.001 1.001 0 1 1 -.005 -2z"/>
              <path d="M9.015 16a1 1 0 0 1 -1 1a1.001 1.001 0 1 1 -.005 -2c.557 0 1.005 .448 1.005 1"/>
            </svg>
            <span data-date-label>${dateLabel}</span>
          </button>
          <input type="date"
                 value="${marker.date}"
                 class="sr-only"
                 data-marker-date
                 data-action="change->lineup-markers#updateDateLabel">

          <button type="button"
                  class="w-8 h-8 rounded-full flex items-center justify-center text-gray-300 hover:bg-red-50 hover:text-red-500 transition"
                  data-action="click->lineup-markers#removeRow"
                  title="Remove">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M20 6a1 1 0 0 1 .117 1.993l-.117 .007h-.081l-.919 11a3 3 0 0 1 -2.824 2.995l-.176 .005h-8c-1.598 0 -2.904 -1.249 -2.992 -2.75l-.005 -.167l-.923 -11.083h-.08a1 1 0 0 1 -.117 -1.993l.117 -.007zm-10 4a1 1 0 0 0 -1 1v6a1 1 0 0 0 2 0v-6a1 1 0 0 0 -1 -1m4 0a1 1 0 0 0 -1 1v6a1 1 0 0 0 2 0v-6a1 1 0 0 0 -1 -1"/>
              <path d="M14 2a2 2 0 0 1 2 2a1 1 0 0 1 -1.993 .117l-.007 -.117h-4l-.007 .117a1 1 0 0 1 -1.993 -.117a2 2 0 0 1 1.85 -1.995l.15 -.005z"/>
            </svg>
          </button>
        </div>
      </div>
    `

    const nameTa = card.querySelector('[data-marker-name]')
    if (nameTa) nameTa.value = marker.name

    return card
  }

  autosize (event) {
    const el = event.target
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
    this.onCardChange()
  }

  autosizeCard (card) {
    requestAnimationFrame(() => {
      card.querySelectorAll('textarea').forEach((el) => {
        el.style.height = 'auto'
        el.style.height = `${el.scrollHeight}px`
      })
    })
  }

  onCardChange () {
    this.serializeMarkers()
    this.updateSubmitButtonState()
  }

  openDatePicker (event) {
    const card = event.currentTarget.closest('[data-marker-index]')
    if (!card) return
    const input = card.querySelector('[data-marker-date]')
    if (!input) return
    if (typeof input.showPicker === 'function') {
      input.showPicker()
    } else {
      input.focus()
      input.click()
    }
  }

  updateDateLabel (event) {
    const input = event.target
    const card = input.closest('[data-marker-index]')
    const label = card?.querySelector('[data-date-label]')
    if (label) {
      label.textContent = input.value ? this.formatDateLabel(input.value) : 'Add date'
    }
    this.onCardChange()
  }

  formatDateLabel (isoDate) {
    if (!isoDate) return 'Add date'
    const [y, m, d] = isoDate.split('-').map(Number)
    const date = new Date(y, m - 1, d)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  addRow () {
    const index = this.cardsBodyTarget.querySelectorAll('[data-marker-index]').length
    const card = this.createMarkerCard({ name: '', date: '' }, index)
    this.cardsBodyTarget.appendChild(card)
    this.autosizeCard(card)
    this.cardsSectionTarget.classList.remove('hidden')
    this.submitButtonVisible = true
    card.querySelector('[data-marker-name]').focus()
    this.updateRowCount()
    this.onCardChange()
  }

  removeRow (event) {
    const card = event.target.closest('[data-marker-index]')
    if (!card) return
    card.remove()
    this.reindexCards()
    this.updateRowCount()
    if (this.markerCount === 0) {
      this.cardsSectionTarget.classList.add('hidden')
      this.submitButtonVisible = false
    }
    this.onCardChange()
  }

  reindexCards () {
    this.cardsBodyTarget.querySelectorAll('[data-marker-index]').forEach((card, index) => {
      card.dataset.markerIndex = index
    })
  }

  updateRowCount () {
    this.rowCountTarget.textContent = String(this.markerCount)
  }

  serializeMarkers () {
    const lines = []
    this.cardsBodyTarget.querySelectorAll('[data-marker-index]').forEach((card) => {
      const name = card.querySelector('[data-marker-name]')?.value.trim() || ''
      const dateInput = card.querySelector('[data-marker-date]')?.value || ''
      if (!name && !dateInput) return
      const date = dateInput ? this.formatDateForSubmit(dateInput) : ''
      lines.push(date ? `${name}, ${date}` : name)
    })
    this.markersContentTarget.value = lines.join('\n')
  }

  formatDateForSubmit (isoDate) {
    const [y, m, d] = isoDate.split('-').map(Number)
    const yy = String(y).slice(-2)
    return `${m}/${d}/${yy}`
  }

  parseDateInput (raw) {
    if (!raw) return ''
    const formats = [
      /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/,
      /^(\d{4})-(\d{1,2})-(\d{1,2})$/
    ]
    let match = raw.match(formats[0])
    if (match) {
      let year = match[3]
      if (year.length === 2) year = parseInt(year) > 50 ? `19${year}` : `20${year}`
      return `${year}-${match[1].padStart(2, '0')}-${match[2].padStart(2, '0')}`
    }
    match = raw.match(formats[1])
    if (match) {
      return `${match[1]}-${match[2].padStart(2, '0')}-${match[3].padStart(2, '0')}`
    }
    return ''
  }

  updateSubmitButtonState () {
    const count = this.validMarkerCount
    this.submitButtonDisabled = count === 0
    this.buttonCountContent = count
  }

  get markerCount () {
    return this.cardsBodyTarget.querySelectorAll('[data-marker-index]').length
  }

  get validMarkerCount () {
    let count = 0
    this.cardsBodyTarget.querySelectorAll('[data-marker-index]').forEach((card) => {
      const name = card.querySelector('[data-marker-name]')?.value.trim()
      const date = card.querySelector('[data-marker-date]')?.value
      if (name && date) count++
    })
    return count
  }

  // eslint-disable-next-line accessor-pairs
  set submitButtonDisabled (value) {
    this.submitButtonTarget.disabled = value
  }

  // eslint-disable-next-line accessor-pairs
  set submitButtonVisible (value) {
    if (value) {
      this.submitButtonTarget.classList.remove('hidden')
    } else {
      this.submitButtonTarget.classList.add('hidden')
    }
  }

  // eslint-disable-next-line accessor-pairs
  set buttonCountContent (count) {
    if (!this.hasButtonCountTarget) return
    this.buttonCountTarget.textContent = count === 1 ? '1 marker' : `${count} markers`
  }
}
